import {
  ReportingTemplateFormula,
  AvailableLineData,
  AvailableLineDataTemplateFormulas,
  ReportTemplateDataReferences,
  ReportTemplateList,
  ReportTemplateDetail,
  ReportTemplateRequest
} from '@futrli-sdk/predict';
import { API_PATHS, interpolatePathParams, REQUEST_METHODS, ROUTE_PARAMS } from '@futrli/shared-services';
import { makeRequestWithResponse } from '../shared';

/**
 * Fetch Report templates
 *
 * @param {string} workspaceId - related workspace id
 * @param {string} organisationId - related organisation id
 * @param {boolean} isConsolidation - flag to specify if we want to call the consolidation endpoint
 * @returns {Promise<>} - Returns templates
 * @throws error
 */
export const fetchReportTemplates = async (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): Promise<ReportTemplateList[]> => {
  const path = interpolatePathParams(
    isConsolidation ? API_PATHS.REPORT_TEMPLATES_CONSOLIDATION : API_PATHS.REPORT_TEMPLATES,
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
    }
  );

  return makeRequestWithResponse<ReportTemplateList[]>({ url: path });
};

/**
 * Fetch Report template by ID
 *
 * @param {string} workspaceId - related workspace id
 * @param {string} organisationId - related organisation id
 * @param {string} id - template id
 * @param {boolean} isConsolidation - flag to specify if we want to call the consolidation endpoint
 * @returns {Promise<>} - Returns template
 * @throws error
 */
export const fetchReportTemplateById = async (
  workspaceId: string,
  organisationId: string,
  id: string,
  isConsolidation?: boolean
): Promise<ReportTemplateDetail> => {
  const path = interpolatePathParams(
    isConsolidation ? API_PATHS.REPORT_TEMPLATE_BY_ID_CONSOLIDATION : API_PATHS.REPORT_TEMPLATE_BY_ID,
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
      [ROUTE_PARAMS.TEMPLATE_ID]: id
    }
  );

  return makeRequestWithResponse<ReportTemplateDetail>({ url: path });
};

/**
 * Create new report template
 *
 * @param {string} workspaceId - related workspace id
 * @param {string} organisationId - related organisation id
 * @param {object} template - template data to create
 * @param {string ('futrli' | 'workspace')} kind - template type
 * @param {boolean} isConsolidation - flag to specify if we want to call the consolidation endpoint
 * @returns {Promise<>} - Returns created template
 * @throws error
 */
export const createNewTemplate = async (
  workspaceId: string,
  organisationId: string,
  template: {
    name: string;
    data?: unknown;
    locale?: string;
    status?: string;
    is_locked?: boolean;
    data_references?: ReportTemplateDataReferences;
    tags?: string[];
    thumbnail_urls?: string[];
    available_area?: string;
    orientation?: 'portrait' | 'landscape' | 'dashboard';
    page_count?: number;
  },
  kind: 'futrli' | 'workspace' = 'futrli',
  isConsolidation?: boolean
  // eslint-disable-next-line complexity
): Promise<ReportTemplateDetail> => {
  const path = interpolatePathParams(
    isConsolidation ? API_PATHS.REPORT_TEMPLATES_CONSOLIDATION : API_PATHS.REPORT_TEMPLATES,
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
    }
  );

  const data: {
    name: string;
    data?: unknown;
    locale?: string;
    status?: string;
    is_locked?: boolean;
    data_references?: ReportTemplateDataReferences;
    kind: 'futrli' | 'workspace';
    tags?: string[];
    thumbnail_urls?: string[];
    available_area?: string;
    orientation: 'portrait' | 'landscape' | 'dashboard';
    page_count: number;
  } = {
    name: template.name || '',
    data: template.data || { pageSetup: { orientation: 'portrait' } },
    locale: template.locale,
    status: template.status || 'draft',
    is_locked: false,
    kind,
    tags: template.tags || [],
    thumbnail_urls: template.thumbnail_urls || [],
    available_area: template.available_area || '',
    orientation: template.orientation || 'portrait',
    page_count: template.page_count ?? 1
  };
  if (template.data_references) {
    data.data_references = template.data_references;
  }

  return makeRequestWithResponse<ReportTemplateDetail>({
    url: path,
    method: REQUEST_METHODS.POST,
    data
  });
};

/**
 * Duplicate report template
 *
 * @param {string} workspaceId - related workspace id
 * @param {string} organisationId - related organisation id
 * @param {string} id - template id
 * @param {boolean} isConsolidation - flag to specify if we want to call the consolidation endpoint
 * @returns {Promise<>} - Returns duplicated template
 * @throws error
 */
export const duplicateReportTemplate = async (
  workspaceId: string,
  organisationId: string,
  id: string,
  name: string,
  isConsolidation?: boolean
): Promise<ReportTemplateDetail> => {
  const path = interpolatePathParams(
    isConsolidation
      ? API_PATHS.REPORT_TEMPLATE_DUPLICATE_BY_ID_CONSOLIDATION
      : API_PATHS.REPORT_TEMPLATE_DUPLICATE_BY_ID,
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
      [ROUTE_PARAMS.TEMPLATE_ID]: id
    }
  );

  return makeRequestWithResponse<ReportTemplateDetail>({
    url: path,
    method: REQUEST_METHODS.POST,
    data: {
      name
    }
  });
};

/**
 * Delete report template
 *
 * @param {string} workspaceId - related workspace id
 * @param {string} organisationId - related organisation id
 * @param {string} id - template id
 * @param {boolean} isConsolidation - flag to specify if we want to call the consolidation endpoint
 * @returns {Promise<>} - Returns Report templates
 * @throws error
 */
export const deleteReportTemplate = async (
  workspaceId: string,
  organisationId: string,
  id: string,
  isConsolidation?: boolean
): Promise<void> => {
  const path = interpolatePathParams(
    isConsolidation ? API_PATHS.REPORT_TEMPLATE_BY_ID_CONSOLIDATION : API_PATHS.REPORT_TEMPLATE_BY_ID,
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
      [ROUTE_PARAMS.TEMPLATE_ID]: id
    }
  );

  return makeRequestWithResponse<void>({
    url: path,
    method: REQUEST_METHODS.DELETE
  });
};

/**
 * Patch report template
 *
 * @param {string} workspaceId - related workspace id
 * @param {string} organisationId - related organisation id
 * @param {string} id - template id
 * @param {object} data - template data to patch
 * @param {boolean} isConsolidation - flag to specify if we want to call the consolidation endpoint
 * @returns {Promise<>} - Returns patched report template
 * @throws error
 */
export const patchReportTemplate = async (
  workspaceId: string,
  organisationId: string,
  id: string,
  data: Partial<ReportTemplateRequest>,
  isConsolidation?: boolean
): Promise<ReportTemplateDetail> => {
  const path = interpolatePathParams(
    isConsolidation ? API_PATHS.REPORT_TEMPLATE_BY_ID_CONSOLIDATION : API_PATHS.REPORT_TEMPLATE_BY_ID,
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
      [ROUTE_PARAMS.TEMPLATE_ID]: id
    }
  );

  return makeRequestWithResponse<ReportTemplateDetail>({
    url: path,
    method: REQUEST_METHODS.PATCH,
    data
  });
};

/**
 * Fetch Templates Available Financial Fields
 *
 * @param {string} workspaceId - related workspace id
 * @param {string} organisationId - related organisation id
 * @param {boolean} isConsolidation - flag to specify if we want to call the consolidation endpoint
 * @returns {Promise<>} - Returns Available Financial Fields
 * @throws error
 */
export const fetchTemplatesAvailableLineData = async (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): Promise<AvailableLineData> => {
  const route = isConsolidation
    ? API_PATHS.AVAILABLE_LINE_DATA_TEMPLATES_CONSOLIDATION
    : API_PATHS.AVAILABLE_LINE_DATA_TEMPLATES_V2;

  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<AvailableLineData>({ url: path });
};

/**
 * Fetch Template Row formula data
 *
 * @param {string} workspaceId - related workspace id
 * @param {string} organisationId - related organisation id
 * @param {boolean} isConsolidation - flag to specify if we want to call the consolidation endpoint
 * @returns {Promise<>} - Returns Available row formula data
 * @throws error
 */
export const fetchAvailableTemplateFormulaData = async (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): Promise<ReportingTemplateFormula[]> => {
  const route = isConsolidation
    ? API_PATHS.AVAILABLE_TEMPLATE_FORMULA_DATA_CONSOLIDATION
    : API_PATHS.AVAILABLE_TEMPLATE_FORMULA_DATA_V2;
  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<ReportingTemplateFormula[]>({ url: path });
};

/**
 * Create a new Template Row formula
 *
 * @param {string} workspaceId - related workspace id
 * @param {string} organisationId - related organisation id
 * @param {object} data - row formula data
 * @param {boolean} isConsolidation - flag to specify if we want to call the consolidation endpoint
 * @returns {Promise<>} - Returns Available row formula data
 * @throws error
 */
export const createNewTemplateFormula = async (
  workspaceId: string,
  organisationId: string,
  data: Partial<ReportingTemplateFormula>,
  isConsolidation?: boolean
): Promise<ReportingTemplateFormula> => {
  const route = isConsolidation
    ? API_PATHS.AVAILABLE_TEMPLATE_FORMULA_DATA_CONSOLIDATION
    : API_PATHS.AVAILABLE_TEMPLATE_FORMULA_DATA_V2;
  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<ReportingTemplateFormula>({ url: path, method: REQUEST_METHODS.POST, data });
};

/**
 * Update a new Template Row formula
 *
 * @param {string} workspaceId - related workspace id
 * @param {string} organisationId - related organisation id
 * @param {string} rowFormulaId - id of row formula to update
 * @param {object} data - row formula data
 * @param {boolean} isConsolidation - flag to specify if we want to call the consolidation endpoint
 * @returns {Promise<>} - Returns Available row formula data
 * @throws error
 */
export const updateTemplateFormula = async (
  workspaceId: string,
  organisationId: string,
  rowFormulaId: string,
  data: Partial<ReportingTemplateFormula>,
  isConsolidation?: boolean
): Promise<ReportingTemplateFormula> => {
  const route = isConsolidation
    ? API_PATHS.TEMPLATE_FORMULA_DATA_BY_ID_CONSOLIDATION
    : API_PATHS.TEMPLATE_FORMULA_DATA_BY_ID_V2;
  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ROW_FORMULA_ID]: rowFormulaId
  });

  return makeRequestWithResponse<ReportingTemplateFormula>({
    url: path,
    method: REQUEST_METHODS.PATCH,
    data
  });
};

/**
 * Delete Template formula
 *
 * @param {string} workspaceId - related workspace id
 * @param {string} organisationId - related organisation id
 * @param {string} rowFormulaId - id of row formula to delete
 * @param {boolean} isConsolidation - flag to specify if we want to call the consolidation endpoint
 */
export const deleteTemplateFormula = async (
  workspaceId: string,
  organisationId: string,
  rowFormulaId: string,
  isConsolidation?: boolean
): Promise<void> => {
  const route = isConsolidation
    ? API_PATHS.TEMPLATE_FORMULA_DATA_BY_ID_CONSOLIDATION
    : API_PATHS.TEMPLATE_FORMULA_DATA_BY_ID_V2;
  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ROW_FORMULA_ID]: rowFormulaId
  });

  return makeRequestWithResponse<void>({
    url: path,
    method: REQUEST_METHODS.DELETE
  });
};

/**
 * Fetch Available line data for template formula
 *
 * @param {string} workspaceId - related workspace id
 * @param {string} organisationId - related organisation id
 * @param {boolean} isConsolidation - flag to specify if we want to call the consolidation endpoint
 * @returns {Promise<>} - Returns Available line data for row formula
 * @throws error
 */
export const fetchAvailableTemplateFormulaLineData = async (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): Promise<AvailableLineDataTemplateFormulas> => {
  const route = isConsolidation
    ? API_PATHS.AVAILABLE_LINE_DATA_TEMPLATE_FORMULA_CONSOLIDATION
    : API_PATHS.AVAILABLE_LINE_DATA_TEMPLATE_FORMULA_V2;

  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<AvailableLineDataTemplateFormulas>({ url: path });
};

/**
 * Bulk publish Template Row formulas
 *
 * @param {string} workspaceId - related workspace id
 * @param {string} organisationId - related organisation id
 * @param {string[]} formulaIds - ids of the template formulas to publish
 * @param {boolean} isConsolidation - flag to specify if we want to call the consolidation endpoint
 * @throws error
 */
export const bulkPublishTemplateFormulas = async (
  workspaceId: string,
  organisationId: string,
  formulaIds: string[],
  isConsolidation?: boolean
) => {
  if (!formulaIds.length) {
    return undefined;
  }
  const path = interpolatePathParams(
    isConsolidation
      ? API_PATHS.TEMPLATE_FORMULA_BULK_PUBLISH_CONSOLIDATION
      : API_PATHS.TEMPLATE_FORMULA_BULK_PUBLISH_V2,
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
    }
  );

  return makeRequestWithResponse<ReportingTemplateFormula>({
    url: path,
    method: REQUEST_METHODS.POST,
    data: { template_formula_ids: formulaIds }
  });
};
